import { resolveClient } from './supabase';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { TenantModuleConfig } from '@lingaai/types';
import { TenantModuleConfigSchema, getNodeTypeModule, getEdgeTypeModule } from '@lingaai/types';

// ============================================
// MODULE PERMISSION CHECKS
// ============================================

/**
 * Get tenant's module configuration
 */
export async function getTenantModuleConfig(tenantId: string, client?: SupabaseClient): Promise<TenantModuleConfig> {
  const supabase = resolveClient(client);

  const { data, error } = await supabase.from('tenants').select('module_config').eq('id', tenantId).single();

  if (error || !data) {
    throw new Error(`Failed to get module config: ${error?.message || 'No data'}`);
  }

  return (data as { module_config: TenantModuleConfig }).module_config;
}

/**
 * Check if a module is enabled for tenant
 */
export function isModuleEnabled(moduleId: string, moduleConfig: TenantModuleConfig): boolean {
  return moduleConfig[moduleId]?.enabled === true;
}

/**
 * Check if a node type is allowed for tenant
 */
export function isNodeTypeAllowed(nodeType: string, moduleConfig: TenantModuleConfig): boolean {
  const moduleId = getNodeTypeModule(nodeType);
  if (!moduleId) return false;

  return isModuleEnabled(moduleId, moduleConfig);
}

/**
 * Check if an edge type is allowed for tenant
 */
export function isEdgeTypeAllowed(edgeType: string, moduleConfig: TenantModuleConfig): boolean {
  const moduleId = getEdgeTypeModule(edgeType);
  if (!moduleId) return false;

  return isModuleEnabled(moduleId, moduleConfig);
}

/**
 * Get all enabled node types for tenant
 */
export function getEnabledNodeTypes(moduleConfig: TenantModuleConfig): string[] {
  const nodeTypes: string[] = [];

  for (const [, config] of Object.entries(moduleConfig)) {
    if (config.enabled && config.node_types) {
      nodeTypes.push(...config.node_types);
    }
  }

  return nodeTypes;
}

/**
 * Get all enabled edge types for tenant
 */
export function getEnabledEdgeTypes(moduleConfig: TenantModuleConfig): string[] {
  const edgeTypes: string[] = [];

  for (const [, config] of Object.entries(moduleConfig)) {
    if (config.enabled && config.edge_types) {
      edgeTypes.push(...config.edge_types);
    }
  }

  return edgeTypes;
}

// ============================================
// MODULE CONFIGURATION OPERATIONS
// ============================================

/**
 * Update tenant module configuration
 */
export async function updateModuleConfig(tenantId: string, moduleId: string, updates: { enabled?: boolean; settings?: Record<string, unknown> }, client?: SupabaseClient): Promise<TenantModuleConfig> {
  const supabase = resolveClient(client);

  // Get current config
  const currentConfig = await getTenantModuleConfig(tenantId, client);

  // Merge updates
  const updatedConfig = {
    ...currentConfig,
    [moduleId]: {
      ...currentConfig[moduleId],
      ...updates,
    },
  };

  // Validate using zod
  const validation = TenantModuleConfigSchema.safeParse(updatedConfig);
  if (!validation.success) {
    throw new Error(`Invalid module configuration: ${validation.error.errors.map((e) => e.message).join(', ')}`);
  }

  // Update
  const { data, error } = await supabase.from('tenants').update({ module_config: updatedConfig }).eq('id', tenantId).select('module_config').single();

  if (error) {
    throw new Error(`Failed to update module config: ${error.message}`);
  }

  return (data as { module_config: TenantModuleConfig }).module_config;
}

/**
 * Enable a module
 */
export async function enableModule(tenantId: string, moduleId: string, client?: SupabaseClient): Promise<TenantModuleConfig> {
  return updateModuleConfig(tenantId, moduleId, { enabled: true }, client);
}

/**
 * Disable a module
 */
export async function disableModule(tenantId: string, moduleId: string, client?: SupabaseClient): Promise<TenantModuleConfig> {
  return updateModuleConfig(tenantId, moduleId, { enabled: false }, client);
}
